import React, { useRef, useEffect, useMemo, useState } from 'react';
import * as d3 from 'd3';
import { sankey, sankeyLinkHorizontal } from 'd3-sankey';
import { getRiskScoreLabel, getUserRiskScore, getUserComputerType } from '../utils';
import { BiExpandAlt, BiCollapse, BiChevronUp, BiChevronDown, BiHide, BiShow } from 'react-icons/bi';

const palette = [
  '#ffcc00', // yellow
  '#c3aed6', // pastel purple
  '#b5e7a0', // pastel green
  '#b3c6e7', // pastel blue
  '#f7cac9', // pastel pink
  '#ffb347', // light orange
  '#b2dfdb', // teal
  '#d4e157', // lime
  '#cfd8dc', // blue grey
  '#f8bbd0', // light pink
];

const getColor = (idx) => palette[idx % palette.length];


// Helper to capitalize only the first letter
const capitalizeFirst = str => str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '';

// Helper to truncate text with ellipsis
const truncateText = (text, maxLength = 15) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const SankeyDiagram = React.memo(({ data, onSankeyFilter, sankeyFilter, currentUserId, isExpanded, onToggleExpand, expansionLevel = 0, onVerticalExpand }) => {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [resizeKey, setResizeKey] = useState(0);
  const [tooltip, setTooltip] = useState({ show: false, content: '', x: 0, y: 0 });
  const [showDomainLabels, setShowDomainLabels] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null); // Track selected link or node: { type: 'link'|'node', data: ... }

  // Reset selection when filter is cleared
  useEffect(() => {
    if (!sankeyFilter) {
      setSelectedItem(null);
    }
  }, [sankeyFilter]);

  // Process data similar to the original component
  const processedData = useMemo(() => {
    const filtered = data.filter(
      rec => rec.bucket === 'leaks.logs' && (rec.domain || rec.source || rec.user) && getUserComputerType(rec.computer_type, currentUserId) && getUserRiskScore(rec.risk_score, currentUserId)
    );

    const removeExtension = str => {
      if (!str) return 'Unknown';
      const s = str.split('.');
      return s.length > 1 ? s.slice(0, -1).join('.') : str;
    };

    const getDomainLabel = rec => (removeExtension(rec.domain || rec.source || rec.user || 'Unknown'));
    const getRiskLabel = score => getRiskScoreLabel ? getRiskScoreLabel(score) : String(score);

    // Build unique lists for each column
    const domainNames = Array.from(new Set(filtered.map(getDomainLabel)));
    const computerTypes = Array.from(new Set(filtered.map(rec => capitalizeFirst(getUserComputerType(rec.computer_type, currentUserId)))));
    const riskScores = Array.from(new Set(filtered.map(rec => getRiskLabel(getUserRiskScore(rec.risk_score, currentUserId)))));

    // Create nodes with proper structure for D3 Sankey
    const nodes = [
      ...domainNames.map((name, i) => ({ 
        id: name, 
        name: name, 
        type: 'domain', 
        color: getColor(i),
        fullName: filtered.find(rec => getDomainLabel(rec) === name)?.domain || 
                 filtered.find(rec => getDomainLabel(rec) === name)?.source || 
                 filtered.find(rec => getDomainLabel(rec) === name)?.user || name
      })),
      ...computerTypes.map((name, i) => ({ 
        id: name, 
        name: name, 
        type: 'computer', 
        color: getColor(i + domainNames.length) 
      })),
      ...riskScores.map((name, i) => ({ 
        id: name, 
        name: name, 
        type: 'risk', 
        color: getColor(i + domainNames.length + computerTypes.length) 
      }))
    ];

    // Build links: domain -> computer type
    // Store risk scores for each link so we can filter precisely later
    const domainToComputer = {};
    const domainToComputerRecords = {}; // Track risk scores for each link
    filtered.forEach(rec => {
      const domain = getDomainLabel(rec);
      const computerType = capitalizeFirst(getUserComputerType(rec.computer_type, currentUserId));
      const riskLabel = getRiskLabel(getUserRiskScore(rec.risk_score, currentUserId));
      const key = `${domain}|${computerType}`;
      domainToComputer[key] = (domainToComputer[key] || 0) + 1;
      if (!domainToComputerRecords[key]) {
        domainToComputerRecords[key] = [];
      }
      domainToComputerRecords[key].push(riskLabel); // Store risk for each record
    });

    // Build links: computer type -> risk score label
    const computerToRisk = {};
    filtered.forEach(rec => {
      const computerType = capitalizeFirst(getUserComputerType(rec.computer_type, currentUserId));
      const riskLabel = getRiskLabel(getUserRiskScore(rec.risk_score, currentUserId));
      const key = `${computerType}|${riskLabel}`;
      computerToRisk[key] = (computerToRisk[key] || 0) + 1;
    });

    // Create links with proper structure for D3 Sankey
    const links = [
      ...Object.entries(domainToComputer).map(([key, value]) => {
        const [sourceId, targetId] = key.split('|');
        return { 
          source: sourceId, 
          target: targetId, 
          value,
          type: 'domain-to-computer',
          riskScores: domainToComputerRecords[key] // Store risk scores for filtering
        };
      }),
      ...Object.entries(computerToRisk).map(([key, value]) => {
        const [sourceId, targetId] = key.split('|');
        return { 
          source: sourceId, 
          target: targetId, 
          value,
          type: 'computer-to-risk',
          riskScore: targetId // The target IS the risk score
        };
      })
    ];

    return { nodes, links, domainNames, computerTypes, riskScores };
  }, [data, currentUserId]);

  useEffect(() => {
    if (!containerRef.current || !svgRef.current || !processedData.nodes.length) {
      // Clear any existing SVG content when there are no nodes
      if (svgRef.current) {
        d3.select(svgRef.current).selectAll("*").remove();
      }
      return;
    }

    const container = containerRef.current;
    const svg = svgRef.current;
    
    // Clear previous content
    d3.select(svg).selectAll("*").remove();

    const width = container.clientWidth;
    const getChartHeight = () => {
      if (!isExpanded) return 220;
      const baseExpandedHeight = 500;
      const expansionIncrement = 150; // Additional height per expansion level
      return baseExpandedHeight + (expansionLevel * expansionIncrement);
    };
    const height = getChartHeight();

    // Set up the SVG
    const svgElement = d3.select(svg)
      .attr('width', width)
      .attr('height', height);

    // Create the Sankey generator
    const sankeyGenerator = sankey()
      .nodeWidth(15)
      .nodePadding(10)
      .extent([[1, 1], [width - 1, height - 25]]); // Leave more space for labels

    // Create a map of node IDs to node objects for quick lookup
    const nodeMap = new Map(processedData.nodes.map(node => [node.id, node]));
    
    // Create valid links with proper node references
    const validLinks = processedData.links
      .map(link => {
        const sourceNode = nodeMap.get(link.source);
        const targetNode = nodeMap.get(link.target);
        
        if (sourceNode && targetNode) {
          return {
            source: sourceNode,
            target: targetNode,
            value: link.value,
            type: link.type, // Preserve type
            riskScores: link.riskScores, // Preserve risk scores for domain-to-computer links
            riskScore: link.riskScore // Preserve risk score for computer-to-risk links
          };
        } else {
          console.warn(`Invalid link: source "${link.source}" or target "${link.target}" not found in nodes`);
          return null;
        }
      })
      .filter(link => link !== null);

    if (validLinks.length === 0) {
      console.warn('No valid links found for Sankey diagram');
      return;
    }

    // Create the data structure for D3 Sankey
    const sankeyData = {
      nodes: processedData.nodes,
      links: validLinks
    };
    
    // Generate the Sankey layout
    const { nodes, links } = sankeyGenerator(sankeyData);

    // Helper to count records with a specific risk score in a link
    const countRecordsWithRisk = (link, targetRisk) => {
      if (!link.riskScores || !Array.isArray(link.riskScores)) return 0;
      return link.riskScores.filter(risk => risk === targetRisk).length;
    };

    // Helper to check if two links are the same
    const isSameLink = (link1, link2) => {
      return link1.source.id === link2.source.id && link1.target.id === link2.target.id;
    };

    // Calculate matching value when a computer-to-risk link is selected
    const getMatchingValueForComputerToRiskLink = (link, selectedLink) => {
      const selectedComputer = selectedLink.source.name;
      const selectedRisk = selectedLink.target.name;

      // If this is the selected link itself, return full value
      if (link.type === 'computer-to-risk' && isSameLink(link, selectedLink)) {
        return link.value;
      }

      // If this is a domain-to-computer link going to the selected computer type
      // Count how many records have the selected risk
      if (link.type === 'domain-to-computer' && link.target.name === selectedComputer) {
        return countRecordsWithRisk(link, selectedRisk);
      }

      return 0;
    };

    // Calculate matching value when a domain-to-computer link is selected
    const getMatchingValueForDomainToComputerLink = (link, selectedLink) => {
      const selectedComputer = selectedLink.target.name;

      // If this is the selected link itself, return full value
      if (link.type === 'domain-to-computer' && isSameLink(link, selectedLink)) {
        return link.value;
      }

      // If this is a computer-to-risk link from the selected computer type
      // Highlight only the portion that represents records from the selected domain-to-computer link
      if (link.type === 'computer-to-risk' && link.source.name === selectedComputer) {
        return countRecordsWithRisk(selectedLink, link.target.name);
      }

      return 0;
    };

    // Calculate matching value when a link is selected
    const getMatchingValueForLinkSelection = (link, selectedLink) => {
      if (selectedLink.type === 'computer-to-risk') {
        return getMatchingValueForComputerToRiskLink(link, selectedLink);
      }
      if (selectedLink.type === 'domain-to-computer') {
        return getMatchingValueForDomainToComputerLink(link, selectedLink);
      }
      return 0;
    };

    // Calculate matching value when a risk node is selected
    const getMatchingValueForRiskNode = (link, targetRisk) => {
      if (link.type === 'computer-to-risk') {
        return link.target.name === targetRisk ? link.value : 0;
      }
      if (link.type === 'domain-to-computer') {
        return countRecordsWithRisk(link, targetRisk);
      }
      return 0;
    };

    // Calculate matching value when a computer node is selected
    const getMatchingValueForComputerNode = (link, targetComputer) => {
      if (link.type === 'domain-to-computer') {
        return link.target.name === targetComputer ? link.value : 0;
      }
      if (link.type === 'computer-to-risk') {
        return link.source.name === targetComputer ? link.value : 0;
      }
      return 0;
    };

    // Calculate matching value when a domain node is selected
    const getMatchingValueForDomainNode = (link, targetDomain) => {
      if (link.type === 'domain-to-computer') {
        return link.source.name === targetDomain ? link.value : 0;
      }
      if (link.type === 'computer-to-risk') {
        // Trace forward: find all domain-to-computer links from the selected domain
        // that go to this computer type, and count how many have the target risk
        const targetComputer = link.source.name;
        const targetRisk = link.target.name;

        const domainToComputerLinks = links.filter(l =>
          l.type === 'domain-to-computer' &&
          l.source.name === targetDomain &&
          l.target.name === targetComputer
        );

        let matchingCount = 0;
        domainToComputerLinks.forEach(dtcLink => {
          matchingCount += countRecordsWithRisk(dtcLink, targetRisk);
        });

        return matchingCount;
      }
      return 0;
    };

    // Calculate matching value when a node is selected
    const getMatchingValueForNodeSelection = (link, selectedNode) => {
      if (selectedNode.type === 'risk') {
        return getMatchingValueForRiskNode(link, selectedNode.name);
      }
      if (selectedNode.type === 'computer') {
        return getMatchingValueForComputerNode(link, selectedNode.name);
      }
      if (selectedNode.type === 'domain') {
        return getMatchingValueForDomainNode(link, selectedNode.name);
      }
      return 0;
    };

    // Main function to calculate matching value for a link based on selection
    const getMatchingValue = (link) => {
      if (!selectedItem) return 0;

      if (selectedItem.type === 'link') {
        return getMatchingValueForLinkSelection(link, selectedItem.data);
      }

      if (selectedItem.type === 'node') {
        return getMatchingValueForNodeSelection(link, selectedItem.data);
      }

      return 0;
    };

    // Helper function to determine if a node should be highlighted
    const isNodeHighlighted = (node) => {
      if (!selectedItem) return false;
      
      if (selectedItem.type === 'node') {
        const selectedNode = selectedItem.data;
        // A node is highlighted if it's the selected node or has highlighted links
        if (selectedNode.id === node.id) return true;
      } else if (selectedItem.type === 'link') {
        const selectedLink = selectedItem.data;
        // For link selection, highlight nodes connected to the selected link
        if (selectedLink.source.id === node.id || selectedLink.target.id === node.id) {
          return true;
        }
      }
      
      // Check if any links connected to this node are highlighted
      const hasHighlightedLink = links.some(link => {
        const isConnected = link.source.id === node.id || link.target.id === node.id;
        return isConnected && getMatchingValue(link) > 0;
      });
      return hasHighlightedLink;
    };

    // Create the link generator
    const linkGenerator = sankeyLinkHorizontal();

    // Create gradients for links
    const defs = svgElement.append("defs");
    
    links.forEach((link, index) => {
      // If nothing is selected, use original opacity. When an item is selected, dim all base links
      const baseOpacity = !selectedItem ? 0.6 : 0.1;
      const midOpacity = !selectedItem ? 0.4 : 0.07;
      
      const gradient = defs.append("linearGradient")
        .attr("id", `link-gradient-${index}`)
        .attr("gradientUnits", "userSpaceOnUse")
        .attr("x1", "0%")
        .attr("y1", "0%")
        .attr("x2", "100%")
        .attr("y2", "0%");
      
      gradient.append("stop")
        .attr("offset", "0%")
        .attr("stop-color", link.source.color)
        .attr("stop-opacity", baseOpacity);

      gradient.append("stop")
        .attr("offset", "50%")
        .attr("stop-color", link.target.color)
        .attr("stop-opacity", midOpacity);
      
      gradient.append("stop")
        .attr("offset", "50%")
        .attr("stop-color", link.source.color)
        .attr("stop-opacity", midOpacity);
      
      gradient.append("stop")
        .attr("offset", "100%")
        .attr("stop-color", link.target.color)
        .attr("stop-opacity", baseOpacity);
    });
    
    // Add base links (dimmed when filtered)
    svgElement.append("g")
      .attr("fill", "none")
      .attr("stroke-opacity", 0.5)
      .selectAll("path")
      .data(links)
      .join("path")
      .attr("d", linkGenerator)
      .attr("stroke", (d, i) => `url(#link-gradient-${i})`)
      .attr("stroke-width", d => Math.max(1, d.width))
      .style("cursor", "pointer")
      .on("click", (event, d) => {
        setSelectedItem({ type: 'link', data: d });
        
        if (d.source.type === 'domain' && d.target.type === 'computer') {
          onSankeyFilter && onSankeyFilter({ 
            domain: d.source.name, 
            computer_type: d.target.name,
            bucket: 'leaks.logs'
          });
        } else if (d.source.type === 'computer' && d.target.type === 'risk') {
          onSankeyFilter && onSankeyFilter({ 
            computer_type: d.source.name, 
            risk_score: d.target.name,
            bucket: 'leaks.logs'
          });
        }
      })
      .on("mouseover", function(event, d) {
        // Only brighten on hover if no node is selected
        if (!selectedItem) {
          const linkIndex = links.indexOf(d);
          const gradient = d3.select(`#link-gradient-${linkIndex}`);
          gradient.selectAll("stop").attr("stop-opacity", 0.9);
        }

        const srcLabel = d.source.type === 'domain' ? `@${String(d.source.fullName || d.source.name).replace(/^@+/, '')}` : (d.source.fullName || d.source.name);
        const tgtLabel = d.target.type === 'domain' ? `@${String(d.target.fullName || d.target.name).replace(/^@+/, '')}` : (d.target.fullName || d.target.name);
        const tooltipContent = `<strong>${srcLabel}</strong> & <strong>${tgtLabel}</strong><br/><strong>Count: ${d.value}</strong>`;
        setTooltip({
          show: true,
          content: tooltipContent,
          x: event.pageX + 10,
          y: event.pageY - 10
        });
      })
      .on("mouseout", function(event, d) {
        const linkIndex = links.indexOf(d);
        const gradient = d3.select(`#link-gradient-${linkIndex}`);
        // When an item is selected, always keep base links dimmed
        const baseOpacity = !selectedItem ? 0.6 : 0.1;
        const midOpacity = !selectedItem ? 0.4 : 0.07;
        
        gradient.selectAll("stop").each(function(d, i) {
          const stop = d3.select(this);
          if (i === 0 || i === 3) {
            stop.attr("stop-opacity", baseOpacity);
          } else {
            stop.attr("stop-opacity", midOpacity);
          }
        });
        setTooltip({ show: false, content: '', x: 0, y: 0 });
      });

    // Add highlighted overlay links (only the matching portion)
    if (selectedItem && (selectedItem.type === 'node' || selectedItem.type === 'link')) {
      const highlightedLinks = links
        .map(link => {
          let matchingValue = 0;
          let matchingRatio = 0;
          
          // Use getMatchingValue for both node and link selection
          // This will trace all related records for link selection
          matchingValue = getMatchingValue(link);
          matchingRatio = link.value > 0 ? matchingValue / link.value : 0;
          
          if (matchingValue > 0) {
            // Create a new link object with the matching value
            // We'll draw an overlay path with thickness proportional to the matching ratio
            return {
              ...link,
              originalValue: link.value,
              value: matchingValue,
              matchingRatio: matchingRatio
            };
          }
          return null;
        })
        .filter(link => link !== null);

      if (highlightedLinks.length > 0) {
        // Create gradients for overlay links (matching base link colors with normal opacity)
        highlightedLinks.forEach((link) => {
          const originalLinkIndex = links.findIndex(l => 
            l.source.id === link.source.id && l.target.id === link.target.id
          );
          const overlayGradient = defs.append("linearGradient")
            .attr("id", `overlay-gradient-${originalLinkIndex}`)
            .attr("gradientUnits", "userSpaceOnUse")
            .attr("x1", "0%")
            .attr("y1", "0%")
            .attr("x2", "100%")
            .attr("y2", "0%");
          
          // Use normal opacity (same as links when nothing is selected) so highlights look natural
          overlayGradient.append("stop")
            .attr("offset", "0%")
            .attr("stop-color", link.source.color)
            .attr("stop-opacity", 0.7);

          overlayGradient.append("stop")
            .attr("offset", "50%")
            .attr("stop-color", link.target.color)
            .attr("stop-opacity", 0.5);
          
          overlayGradient.append("stop")
            .attr("offset", "50%")
            .attr("stop-color", link.source.color)
            .attr("stop-opacity", 0.5);
          
          overlayGradient.append("stop")
            .attr("offset", "100%")
            .attr("stop-color", link.target.color)
            .attr("stop-opacity", 0.7);
        });

        // Draw highlighted overlay paths on top of the base links
        // The thickness (height) of each overlay is scaled by the matching ratio
        const highlightedGroup = svgElement.append("g")
          .attr("fill", "none")
          .attr("stroke-opacity", 0.6);

        highlightedGroup.selectAll("path")
          .data(highlightedLinks)
          .join("path")
          .attr("d", linkGenerator)
          .attr("stroke", (d) => {
            // Use the gradient that matches the base link colors
            const originalLinkIndex = links.findIndex(l => 
              l.source.id === d.source.id && l.target.id === d.target.id
            );
            return `url(#overlay-gradient-${originalLinkIndex})`;
          })
          .attr("stroke-width", d => {
            // Scale the height (thickness) by the matching ratio
            // In a horizontal Sankey, d.width is actually the vertical thickness (height) of the link
            const baseThickness = Math.max(1, d.width);
            const scaledThickness = baseThickness * d.matchingRatio;
            // Ensure minimum visible thickness
            return Math.max(2, scaledThickness);
          })
          .style("pointer-events", "none"); // Allow mouse events to pass through to base links
      }
    }

    // Add nodes
    svgElement.append("g")
      .selectAll("rect")
      .data(nodes)
      .join("rect")
      .attr("x", d => d.x0)
      .attr("y", d => d.y0)
      .attr("height", d => d.y1 - d.y0)
      .attr("width", d => d.x1 - d.x0)
      .attr("fill", d => d.color)
      .attr("opacity", d => {
        // If nothing is selected, use original opacity (1.0). Otherwise, highlight selected and dim others
        if (!selectedItem) return 1.0;
        const isHighlighted = isNodeHighlighted(d);
        return isHighlighted ? 1.0 : 0.2;
      })
      .style("cursor", "pointer")
      .on("click", (event, d) => {
        // Set selected item for highlighting
        setSelectedItem({ type: 'node', data: d });
        
        // Handle node click
        if (d.type === 'domain') {
          onSankeyFilter && onSankeyFilter({ domain: d.name, bucket: 'leaks.logs' });
        } else if (d.type === 'computer') {
          onSankeyFilter && onSankeyFilter({ computer_type: d.name, bucket: 'leaks.logs' });
        } else if (d.type === 'risk') {
          onSankeyFilter && onSankeyFilter({ risk_score: d.name, bucket: 'leaks.logs' });
        }
      })
      .on("mouseover", function(event, d) {
        // Change opacity on hover - use 0.8 if nothing is selected, or if selected but not this node
        if (!selectedItem || !isNodeHighlighted(d)) {
          d3.select(this).attr("opacity", 0.8);
        }
        
        // Show tooltip for nodes
        let tooltipContent = '';
        if (d.type === 'domain') {
          const fullName = d.fullName || d.name;
          tooltipContent = `<strong>Domain:</strong> @${String(fullName).replace(/^@+/, '')}`;
        } else if (d.type === 'computer') {
          tooltipContent = `<strong>Computer Type:</strong> ${d.name}`;
        } else if (d.type === 'risk') {
          tooltipContent = `<strong>Risk Score:</strong> ${d.name}`;
        }
        
        setTooltip({
          show: true,
          content: tooltipContent,
          x: event.pageX + 10,
          y: event.pageY - 10
        });
      })
      .on("mouseout", function(event, d) {
        // Reset opacity based on selection state
        // If nothing is selected, use original opacity (1.0). Otherwise, highlight selected and dim others
        if (!selectedItem) {
          d3.select(this).attr("opacity", 1.0);
        } else {
          const isHighlighted = isNodeHighlighted(d);
          d3.select(this).attr("opacity", isHighlighted ? 1.0 : 0.3);
        }
        setTooltip({ show: false, content: '', x: 0, y: 0 });
      });

    // Calculate font size: only increase on initial expand, not arrow levels
    const getNodeLabelFontSize = () => {
      const baseFontSize = 10;
      let expansionMultiplier = 1;
      
      // Increase font size when first expanded (isExpanded)
      if (isExpanded) {
        expansionMultiplier += 0.4; // 40% increase when first expanded
      }
      
      return Math.round(baseFontSize * expansionMultiplier);
    };

    // Add node labels
    svgElement.append("g")
    .attr("font-family", "sans-serif")
    .attr("fill", "#fff")
    .selectAll("text")
    .data(nodes)
    .join("text")
    .attr("x", d => d.x0 < width / 2 ? d.x1 + 6 : d.x0 - 6)
    .attr("y", d => (d.y1 + d.y0) / 2)
    .attr("dy", "0.35em")
    .attr("text-anchor", d => d.x0 < width / 2 ? "start" : "end")
    .attr("font-size", getNodeLabelFontSize()) // Move this line here
    .text(d => {
      // Hide domain labels if toggle is off
      if (d.type === 'domain' && !showDomainLabels) {
        return '';
      }
      // When a filter that includes a domain is active, hide labels of other domains
      if (d.type === 'domain' && sankeyFilter?.domain && d.name !== sankeyFilter.domain) {
        return '';
      }
      const displayText = d.name;
      // Apply truncation only to domain nodes, with dynamic max length based on expansion
      const maxLength = isExpanded ? 20 + (expansionLevel * 5) : 15;
      return d.type === 'domain' ? truncateText(displayText, maxLength) : displayText;
    })
    .style("pointer-events", "none");

    // Calculate column label font size based on expansion level
    const getColumnLabelFontSize = () => {
      const baseFontSize = 12;
      let expansionMultiplier = 1;
      
      // Increase font size when first expanded (isExpanded) only
      if (isExpanded) {
        expansionMultiplier += 0.3; // 30% increase when first expanded
      }
      
      // No additional increase per vertical expansion level for column labels
      
      return Math.round(baseFontSize * expansionMultiplier);
    };

    // Add column labels
    const columnLabels = [
      { text: "Domain", x: width * 0.05 },
      { text: "Profile Type", x: width * 0.5 },
      { text: "Risk", x: width * 0.95 }
    ];

    svgElement.append("g")
      .attr("font-family", "sans-serif")
      .attr("font-size", getColumnLabelFontSize())
      .attr("fill", "#999")
      .selectAll("text")
      .data(columnLabels)
      .join("text")
      .attr("x", d => d.x)
      .attr("y", height - 10)
      .attr("text-anchor", "middle")
      .text(d => d.text);

  }, [processedData, isExpanded, expansionLevel, onSankeyFilter, resizeKey, showDomainLabels, selectedItem, sankeyFilter]);

  // Handle resize
  useEffect(() => {
    if (!containerRef.current) return;
    
    const resizeObserver = new ResizeObserver(() => {
      // Force re-render by updating the resize key
      setResizeKey(prev => prev + 1);
    });
    
    resizeObserver.observe(containerRef.current);
    
    return () => resizeObserver.disconnect();
  }, []);

  // Calculate height based on expansion state and level
  const getChartHeight = () => {
    if (!isExpanded) return 220;
    const baseExpandedHeight = 500;
    const expansionIncrement = 150; // Additional height per expansion level
    return baseExpandedHeight + (expansionLevel * expansionIncrement);
  };

  const MaxExpansionLevel = 20;

  return (
    <div
      ref={containerRef}
      className="time-series-container"
      style={{ 
        width: '100%', 
        height: getChartHeight(),
        position: 'relative',
        transition: 'height 0.3s ease-in-out'
      }}
      onTransitionEnd={() => {
        // Force re-render when transition ends
        setResizeKey(prev => prev + 1);
      }}
    >
      <div style={{
        position: 'absolute',
        top: -30,
        right: 8,
        display: 'flex',
        gap: 4,
        zIndex: 10
      }}>
        {/* Domain labels toggle button */}
        <button
          onClick={() => setShowDomainLabels(!showDomainLabels)}
          style={{
            background: 'none',
            border: 'none',
            borderRadius: '4px',
            color: '#999',
            padding: '6px',
            fontSize: '16px',
            cursor: 'pointer',
            transition: 'color 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '28px',
            minHeight: '28px',
          }}
          onMouseEnter={(e) => e.target.style.color = '#ddd'}
          onMouseLeave={(e) => e.target.style.color = '#999'}
          title={showDomainLabels ? "Hide domain labels" : "Show domain labels"}
        >
          {showDomainLabels ? <BiHide /> : <BiShow />}
        </button>

        {/* Vertical expansion controls - only show when expanded */}
        {isExpanded && onVerticalExpand && (
          <>
            <button
              onClick={() => onVerticalExpand('up')}
              disabled={expansionLevel <= 0}
              style={{
                background: 'none',
                border: 'none',
                borderRadius: '4px',
                color: expansionLevel <= 0 ? '#666' : '#999',
                padding: '6px',
                fontSize: '16px',
                cursor: expansionLevel <= 0 ? 'not-allowed' : 'pointer',
                transition: 'color 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '28px',
                minHeight: '28px',
                opacity: expansionLevel <= 0 ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (expansionLevel > 0) e.target.style.color = '#ddd';
              }}
              onMouseLeave={(e) => {
                if (expansionLevel > 0) e.target.style.color = '#999';
              }}
              title="Reduce height"
            >
              <BiChevronUp />
            </button>
            <button
              onClick={() => onVerticalExpand('down')}
              disabled={expansionLevel >= MaxExpansionLevel}
              style={{
                background: 'none',
                border: 'none',
                borderRadius: '4px',
                color: expansionLevel >= MaxExpansionLevel ? '#666' : '#999',
                padding: '6px',
                fontSize: '16px',
                cursor: expansionLevel >= MaxExpansionLevel ? 'not-allowed' : 'pointer',
                transition: 'color 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '28px',
                minHeight: '28px',
                opacity: expansionLevel >= MaxExpansionLevel ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (expansionLevel < MaxExpansionLevel) e.target.style.color = '#ddd';
              }}
              onMouseLeave={(e) => {
                if (expansionLevel < MaxExpansionLevel) e.target.style.color = '#999';
              }}
              title="Increase height"
            >
              <BiChevronDown />
            </button>
          </>
        )}
        
        {/* Main expand/collapse button */}
        <button
          onClick={onToggleExpand}
          style={{
            background: 'none',
            border: 'none',
            borderRadius: '4px',
            color: '#999',
            padding: '8px',
            fontSize: '18px',
            cursor: 'pointer',
            transition: 'color 0.2s',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '32px',
            minHeight: '32px',
          }}
          onMouseEnter={(e) => e.target.style.color = '#ddd'}
          onMouseLeave={(e) => e.target.style.color = '#999'}
          title={isExpanded ? "Collapse" : "Expand"}
        >
          {isExpanded ? (
            <BiCollapse />
          ) : (
            <BiExpandAlt />
          )}
        </button>
      </div>
      {processedData.nodes.length > 0 ? (
        <svg
          ref={svgRef}
          style={{
            width: '100%',
            height: '100%',
            background: 'transparent'
          }}
        />
      ) : (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: '#666',
          fontSize: '16px',
          fontFamily: 'sans-serif',
          textAlign: 'center',
          padding: '20px'
        }}>
          No InfoStealer Infections Found
        </div>
      )}
      
      {/* Tooltip */}
      {tooltip.show && (
        <div
          style={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '4px',
            fontSize: '12px',
            fontFamily: 'sans-serif',
            zIndex: 1000,
            pointerEvents: 'none',
            maxWidth: '200px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
          dangerouslySetInnerHTML={{ __html: tooltip.content }}
        />
      )}
    </div>
  );
});

export default SankeyDiagram; 